/**
 * AmenitizAvailability — widget de réservation intégré (zéro redirection).
 * Calendrier interactif 30 jours : choisir arrivée + départ → prix total live
 * (API Amenitiz) → paiement dans une fenêtre intégrée Amenitiz (iframe).
 */
import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { track, EVENTS } from "../lib/events";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

/** Trace un départ vers Amenitiz (analytics interne + GA4 si consenti). */
export function trackAmenitizClick(source, roomId) {
  track(EVENTS.CLICK_AMENITIZ, { source, ...(roomId ? { room_id: roomId } : {}) });
  try {
    if (typeof window.gtag === "function") window.gtag("event", "click_amenitiz", { source });
  } catch { /* GA absent */ }
}

const DAY_LABELS = ["D", "L", "M", "M", "J", "V", "S"];

/** Moteur de réservation Amenitiz générique (fallback quand aucune URL de fiche n'est renseignée). */
export const DEFAULT_BOOKING_URL = "https://domaine-de-la-briouse.amenitiz.io";

/** URL directe du moteur de réservation (saute la page d'accueil Amenitiz + son bandeau). */
export function buildEngineUrl(bookingUrl, checkin, checkout, roomId) {
  if (!bookingUrl) return null;
  const [rawBase, rawHash] = bookingUrl.replace(/\/+$/, "").split("#");
  const base = rawBase.replace(/\/+$/, "");
  const isDirect = /\/booking(\/|$)/.test(base); // URL déjà spécifique (fiche hébergement)
  const params = new URLSearchParams();
  if (checkin && checkout) { params.set("checkin", checkin); params.set("checkout", checkout); }
  if (roomId && !isDirect) params.set("info", roomId);
  const qs = params.toString();
  if (isDirect) return `${base}${qs ? `?${qs}` : ""}#${rawHash || "DatesGuests-BE"}`;
  return `${base}/fr/booking/room${qs ? `?${qs}` : ""}#DatesGuests-BE`;
}

const CROP_TOP = 88; // hauteur (px) de l'en-tête Amenitiz masqué par recadrage

export function BookingEngineModal({ open, onOpenChange, bookingUrl, checkin, checkout, roomId }) {
  const engineUrl = buildEngineUrl(bookingUrl, checkin, checkout, roomId);
  const fmt = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long" }) : null);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!max-w-4xl bg-lin border-pierre p-0 overflow-hidden" data-testid="booking-engine-modal">
        <DialogHeader className="px-4 pt-3 pb-2">
          <DialogTitle className="font-serif text-domaine text-lg">
            Finaliser la réservation
            {checkin && checkout && (
              <span className="block font-sans text-xs text-ardoise/60 font-normal mt-1" data-testid="booking-modal-dates">
                {fmt(checkin)} → {fmt(checkout)} · paiement sécurisé
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        {engineUrl && (
          <div className="w-full overflow-hidden bg-white" style={{ height: "72vh" }}>
            <iframe
              src={engineUrl}
              title="Moteur de réservation Amenitiz"
              data-testid="booking-engine-iframe"
              className="w-full border-0"
              style={{ height: `calc(72vh + ${CROP_TOP}px)`, marginTop: `-${CROP_TOP}px` }}
            />
          </div>
        )}
        <div className="px-4 py-2 text-right">
          <a href={engineUrl} target="_blank" rel="noopener noreferrer"
             onClick={() => trackAmenitizClick("engine_new_tab", roomId)}
             className="text-[10px] text-ardoise/50 underline hover:text-domaine" data-testid="booking-open-new-tab">
            Ouvrir dans un nouvel onglet ↗
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const MAX_OFFSET = 150; // ~6 mois de calendrier extensible

export default function AmenitizAvailability({ roomId, days = 30 }) {
  const [state, setState] = useState({ loading: true, dates: [], bookingUrl: null });
  const [offset, setOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sel, setSel] = useState({ checkin: null, checkout: null });
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [showEngine, setShowEngine] = useState(false);

  useEffect(() => {
    if (!roomId) return;
    let alive = true;
    setOffset(0);
    api.get(`/public/amenitiz/availability/${roomId}`, { params: { days } })
      .then((r) => {
        if (alive) setState({
          loading: false,
          dates: r.data?.status === "ok" ? (r.data.dates || []) : [],
          bookingUrl: r.data?.booking_url || null,
        });
      })
      .catch(() => alive && setState({ loading: false, dates: [], bookingUrl: null }));
    return () => { alive = false; };
  }, [roomId, days]);

  const extendCalendar = () => {
    const next = offset + 30;
    setLoadingMore(true);
    api.get(`/public/amenitiz/availability/${roomId}`, { params: { days: 30, offset: next } })
      .then((r) => {
        const extra = r.data?.status === "ok" ? (r.data.dates || []) : [];
        setState((s) => {
          const seen = new Set(s.dates.map((d) => d.date));
          return { ...s, dates: [...s.dates, ...extra.filter((d) => !seen.has(d.date))] };
        });
        setOffset(next);
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  useEffect(() => {
    if (!sel.checkin || !sel.checkout) { setQuote(null); return; }
    let alive = true;
    setQuoting(true);
    api.get("/public/amenitiz/quote", { params: { room_id: roomId, checkin: sel.checkin, checkout: sel.checkout } })
      .then((r) => alive && setQuote(r.data))
      .catch(() => alive && setQuote(null))
      .finally(() => alive && setQuoting(false));
    return () => { alive = false; };
  }, [sel.checkin, sel.checkout, roomId]);

  if (!roomId) return null;

  // Fallback : si les disponibilités Amenitiz sont indisponibles (API en erreur),
  // on affiche quand même un bouton direct vers le moteur de réservation.
  if (!state.loading && state.dates.length === 0) {
    const directUrl = buildEngineUrl(state.bookingUrl || DEFAULT_BOOKING_URL, null, null, null);
    return (
      <div className="mt-6" data-testid="amenitiz-availability-fallback">
        <p className="text-[11px] uppercase tracking-[0.3em] text-or mb-2">
          Réserver ce séjour
        </p>
        <a href={directUrl} target="_blank" rel="noopener noreferrer"
           data-testid="booking-direct-link"
           onClick={() => trackAmenitizClick("fallback_direct", roomId)}
           className="inline-flex items-center justify-center px-6 py-3 bg-domaine text-lin text-sm uppercase tracking-widest hover:bg-or transition-colors">
          Réserver — paiement sécurisé ↗
        </a>
        <p className="text-[11px] text-ardoise/50 mt-2">
          Vous serez redirigé vers notre plateforme de réservation sécurisée.
        </p>
      </div>
    );
  }

  const availMap = Object.fromEntries(state.dates.map((d) => [d.date, d.available]));

  const pick = (d) => {
    if (!sel.checkin || (sel.checkin && sel.checkout)) {
      if (!d.available) return;
      setSel({ checkin: d.date, checkout: null });
      return;
    }
    if (d.date <= sel.checkin) {
      if (d.available) setSel({ checkin: d.date, checkout: null });
      return;
    }
    // toutes les nuits entre checkin et d doivent être libres
    const nights = state.dates.filter((x) => x.date >= sel.checkin && x.date < d.date);
    if (nights.every((x) => availMap[x.date])) {
      setSel({ checkin: sel.checkin, checkout: d.date });
    }
  };

  const inRange = (date) =>
    sel.checkin && sel.checkout && date >= sel.checkin && date < sel.checkout;

  return (
    <div className="mt-6" data-testid="amenitiz-availability">
      <p className="text-[11px] uppercase tracking-[0.3em] text-or mb-1">
        Réserver ce séjour
      </p>
      <p className="text-[11px] text-ardoise/60 mb-2">
        {!sel.checkin ? "1. Choisissez votre date d'arrivée" :
         !sel.checkout ? "2. Choisissez votre date de départ" :
         "Dates sélectionnées — vérifiez le tarif ci-dessous"}
      </p>
      {state.loading ? (
        <p className="text-xs text-ardoise/50">Chargement des disponibilités…</p>
      ) : (
        <>
          <div className="grid grid-cols-7 gap-1">
            {state.dates.map((d) => {
              const dt = new Date(d.date + "T00:00:00");
              const selected = d.date === sel.checkin || d.date === sel.checkout || inRange(d.date);
              return (
                <button
                  type="button"
                  key={d.date}
                  onClick={() => pick(d)}
                  title={`${dt.toLocaleDateString("fr-FR")} — ${d.available ? "Disponible" : "Complet"}`}
                  data-testid={`avail-day-${d.date}`}
                  className={`flex flex-col items-center justify-center py-1 text-[10px] leading-tight border transition-colors
                              ${selected
                                ? "border-domaine bg-domaine text-lin"
                                : d.available
                                  ? "border-or/50 bg-or/10 text-domaine hover:bg-or/30 cursor-pointer"
                                  : "border-pierre/40 bg-ardoise/5 text-ardoise/35 line-through cursor-not-allowed"}`}
                >
                  <span className="text-[8px] uppercase">
                    {dt.getDate() === 1 ? dt.toLocaleDateString("fr-FR", { month: "short" }) : DAY_LABELS[dt.getDay()]}
                  </span>
                  <span>{dt.getDate()}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-2 flex items-center gap-4 text-[10px] text-ardoise/60">
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-3 border border-or/50 bg-or/10" /> Disponible
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-3 border border-pierre/40 bg-ardoise/5" /> Complet
            </span>
            {offset < MAX_OFFSET && (
              <button type="button" onClick={extendCalendar} disabled={loadingMore}
                      className="underline text-or hover:text-domaine disabled:opacity-50"
                      data-testid="booking-extend-calendar">
                {loadingMore ? "Chargement…" : "+ 30 jours"}
              </button>
            )}
            {sel.checkin && (
              <button type="button" onClick={() => setSel({ checkin: null, checkout: null })}
                      className="ml-auto underline text-ardoise/50 hover:text-domaine" data-testid="booking-reset-dates">
                Réinitialiser
              </button>
            )}
          </div>

          {(!sel.checkin || !sel.checkout) && (
            <button
              type="button"
              onClick={() => setShowEngine(true)}
              data-testid="booking-direct-btn"
              className="mt-4 w-full px-6 py-3 border border-domaine text-domaine text-xs uppercase tracking-[0.25em] hover:bg-domaine hover:text-lin transition-colors"
            >
              Réserver ce logement
            </button>
          )}

          {sel.checkin && sel.checkout && (
            <div className="mt-4 border border-or/40 bg-sable/30 p-4" data-testid="booking-quote">
              {quoting ? (
                <p className="text-xs text-ardoise/60">Calcul du tarif…</p>
              ) : quote ? (
                <>
                  <div className="flex items-baseline justify-between">
                    <span className="text-xs text-ardoise/70">
                      {new Date(sel.checkin + "T00:00:00").toLocaleDateString("fr-FR")} → {new Date(sel.checkout + "T00:00:00").toLocaleDateString("fr-FR")} · {quote.nights} nuit{quote.nights > 1 ? "s" : ""}
                    </span>
                    {quote.total != null && (
                      <span className="font-serif text-2xl text-domaine" data-testid="booking-quote-total">
                        {quote.total.toLocaleString("fr-FR")} €
                      </span>
                    )}
                  </div>
                  {quote.available === false && (
                    <p className="text-xs text-red-700 mt-1">Ces dates ne sont plus disponibles.</p>
                  )}
                  <button
                    type="button"
                    disabled={quote.available === false}
                    onClick={() => { trackAmenitizClick("engine_modal", roomId); setShowEngine(true); }}
                    data-testid="booking-pay-btn"
                    className="mt-3 w-full px-6 py-3 bg-domaine text-lin text-xs uppercase tracking-[0.25em] hover:bg-or hover:text-domaine transition-colors disabled:opacity-40"
                  >
                    Réserver — paiement sécurisé
                  </button>
                  <p className="text-[10px] text-ardoise/50 mt-2 text-center">
                    Paiement et confirmation instantanés via Amenitiz, sans quitter le site.
                  </p>
                </>
              ) : (
                <p className="text-xs text-ardoise/60">Tarif indisponible pour ces dates.</p>
              )}
            </div>
          )}
        </>
      )}
      <BookingEngineModal open={showEngine} onOpenChange={setShowEngine} bookingUrl={state.bookingUrl}
                          checkin={sel.checkin} checkout={sel.checkout} roomId={roomId} />
    </div>
  );
}
